import { StrictMode, FC, useContext } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import App from '@/App';
import { AppContext, AuthenticatedUserContext, FormsFeatureContentProvider, FormsFeatureProvider, FormsMetadataContext, IFormsFeatureContentFallbackProps, IFormsFeatureLoading } from '@ngt/forms'
import formsFeature from '@/formsFeature';
import { LinearProgress } from '@mui/material';
import { SWRConfig } from 'swr';
import AppSettingsProvider from './components/providers/AppSettingsProvider';
import { GetAppSettingsResponse } from './api/dtos';
import initializeSentry from './utils/initializeSentry';
import { getSentrySettingsFromEnv } from './AppSettings';

declare let global: any;

if (typeof global === 'undefined') {
    (window as any).global = window;
}

export const IsLoading: IFormsFeatureLoading = (
    { loading: appLoading, error: appError}, 
    { loading: formsMetadataLoading, error: formsMetadataError }
) => {
    return appLoading || !!appError || formsMetadataLoading || !!formsMetadataError;
};


export const Fallback: FC<IFormsFeatureContentFallbackProps> = () => {

    const { loading: formsMetadataLoading, error: formsMetadataError } = useContext(FormsMetadataContext);
    const { loading: authenticatedUserLoading } = useContext(AuthenticatedUserContext);
    const { loading: appLoading, error: appError} = useContext(AppContext);

    if (formsMetadataError?.errorCode === '401' ||
        appError?.errorCode === '401') {
        window.location.href = '/auth/openiddict';

        return (
            <>
                <LinearProgress />
            </>
        );
    }

    if (!!formsMetadataError || !!appError) {
        return (
            <>
                <LinearProgress />
            </>
        );
    }

    if (formsMetadataLoading || authenticatedUserLoading || appLoading) {
        return (
            <>
                <LinearProgress />
            </>
        );
    }

    return (
        <>
            <LinearProgress />
        </>
    )
}

// Initialize Sentry before React renders so errors thrown during the
// first render pass are captured. useSentryInitializer acts as a fallback
// in case sentrySettings are absent from the initial window.APPSETTINGS.
const initialAppSettings = (window as any).APPSETTINGS as GetAppSettingsResponse | undefined;
const sentrySettings = initialAppSettings?.sentrySettings ?? getSentrySettingsFromEnv();

if (sentrySettings) {
    initializeSentry(sentrySettings);
}

const root = createRoot(document.getElementById('root')!, 
    { 
        onRecoverableError: Sentry.reactErrorHandler() 
    });
    
root.render(
    <StrictMode>
        <SWRConfig 
            value={{    
                revalidateIfStale: false,
                revalidateOnFocus: false,
                revalidateOnReconnect: false
            }}
        >
            <FormsFeatureProvider value={formsFeature}>
                <FormsFeatureContentProvider
                    fallback={Fallback}
                    isLoading={IsLoading}
                >
                    <AppSettingsProvider initial={(window as any).APPSETTINGS}>
                        <App />
                    </AppSettingsProvider>
                </FormsFeatureContentProvider>
            </FormsFeatureProvider>
        </SWRConfig>
    </StrictMode>
);